import React, { useState } from 'react';
import { Popover } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTheme } from 'admin/components/common/Theme';
import {
	getDesignPresetInheritedAssignment,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
} from 'common/design-tokens';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import t from 'utils/translate';
import { getDesignPresetDisplayValue } from './design-preset-display';
import type { PresetAssignmentChange, PresetPropertyGroup } from './design-preset-editing';
import { PlatformAssignment } from './DesignPresetAssignment';
import DesignPresetBorderControl from './DesignPresetBorderControl';
import DesignPresetSpacingControl from './DesignPresetSpacingControl';
import css from './DesignPresetsEditor.scss';

export default function DesignPresetGroupCell({
	group,
	preset,
	platform,
	platformLabel,
	design,
	mediaQuery,
	modeSelection,
	fonts,
	disabled,
	onChange,
}: {
	group: PresetPropertyGroup;
	preset: DesignPreset;
	platform: 'base' | StoryDefaultPlatform;
	platformLabel: string;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	modeSelection: Record<string, string>;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	onChange: (changes: readonly PresetAssignmentChange[]) => void;
}) {
	const [open, setOpen] = useState(false);
	const { theme } = useTheme();
	const summary = group.properties.flatMap(property => {
		const assignment = preset.properties[property];
		if (!assignment) return [];
		const display = getDesignPresetDisplayValue({
			property,
			assignment,
			platform,
			mediaQuery,
			design,
			modeSelection,
		});
		if (!display) return [];
		const name =
			property === 'borderRadius' ? 'All corners' : t(`presets.properties.${property}`).split(' · ').pop();
		const value =
			typeof display.value === 'string' && /^[-+]?[\d.]+px$/.test(display.value)
				? `${Number(parseFloat(display.value).toFixed(1))}px`
				: display.value;
		return [`${name}: ${value}`];
	});
	const assignment = (property: DesignPresetProperty) => (
		<PlatformAssignment
			property={property}
			preset={preset}
			platform={platform}
			design={design}
			mediaQuery={mediaQuery}
			modeSelection={modeSelection}
			fonts={fonts}
			disabled={disabled}
			onChange={value => onChange([{ property, value }])}
		/>
	);
	const field = (property: DesignPresetProperty) => (
		<div className={css.groupField} key={property}>
			<span>
				{property === 'borderRadius' ? 'All corners' : t(`presets.properties.${property}`).split(' · ').pop()}
			</span>
			{assignment(property)}
		</div>
	);
	const spacing = group.id === 'innerSpace' || group.id === 'outerSpace';
	const border = preset.properties.borderStyle ?? {};
	const borderValue =
		platform === 'base'
			? border.base
			: border.platforms?.[platform] ?? getDesignPresetInheritedAssignment(border, platform, mediaQuery)?.value;
	const hasAssignments = group.properties.some(property => {
		const value = preset.properties[property];
		return platform === 'base' ? value?.base !== undefined : value?.platforms?.[platform] !== undefined;
	});
	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			open={open && !disabled}
			onOpenChange={setOpen}
			styles={{ container: { padding: 0 } }}
			content={
				open ? (
					<div
						className={css.groupEditor}
						data-theme={theme}
						data-spacing={spacing}
						onKeyDown={event => {
							if (event.key === 'Escape') {
								event.stopPropagation();
								setOpen(false);
							}
						}}
					>
						<strong>
							{group.label} · {platformLabel}
						</strong>
						{spacing ? (
							<DesignPresetSpacingControl
								properties={group.properties}
								preset={preset}
								platform={platform}
								design={design}
								mediaQuery={mediaQuery}
								modeSelection={modeSelection}
								fonts={fonts}
								disabled={disabled}
								onChange={onChange}
							/>
						) : group.id === 'border' ? (
							<>
								<DesignPresetBorderControl
									value={typeof borderValue === 'string' ? borderValue : undefined}
									assigned={
										platform === 'base'
											? border.base !== undefined
											: border.platforms?.[platform] !== undefined
									}
									disabled={disabled}
									onChange={value => onChange([{ property: 'borderStyle', value }])}
								/>
								{field('borderWidth')}
								{field('borderColor')}
							</>
						) : (
							group.properties.map(property => field(property))
						)}
						<span className={css.groupHint}>Unassigned fields inherit. Clear to reset.</span>
					</div>
				) : null
			}
		>
			<button
				type="button"
				className={css.groupTrigger}
				disabled={disabled}
				data-inherited={!hasAssignments}
				aria-label={`Edit ${group.label} · ${platformLabel}`}
				onClick={() => setOpen(!open)}
			>
				<span>{summary.join(' · ') || 'Configure'}</span>
				<EditOutlined />
			</button>
		</Popover>
	);
}
